import { createWalletInteractionAnalytics } from "../../utils/walletInteractionAnalytics";
import React, { useState, useEffect, useRef } from "react";
import { FormBlock, CardInputWrapper, Paragraph } from "./styled";
import buttonIconDisabled from "../../assets/images/paywall/icon_button_dis.svg";
import questionIcon from "../../assets/images/paywall/question_icon.svg";
import cvv from "../../assets/images/paywall/cvv_icon.png";
import Button from "./../Button/Button";
import { useOnOutsideClick } from "../../hooks/useOutsideClick";
import axios from "axios";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { checkValueToType } from "../../utils/validators";
import cardsIcons from "./../../assets/images/common/cards.png";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/ja";
import { texts } from "../../constants/textsLoaction";
import { API_URL, EU_STRIPE_PRICE_ID, STRIPE_PRICE_ID, TRIAL_DAYS } from "../../constants/formConstants";
import { setApplePayAvailible } from "../../store/reducers/payment";
import { createLegacySubscriptionCustomer, postLegacySubscription } from "../../utils/legacySubscription";
import { getAcquisitionCountryMetadataFromSearch } from "../../utils/acquisitionCountry";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../../utils/checkoutMetadata";
import { pushPurchaseEvent } from "../../utils/gtm";
import { getClientReferenceId, sendAmplitudePurchase } from "../../utils/amplitude";
import { DEFAULT_WALLET_PAYMENT_LABEL, getPaywallContext } from "../../utils/paywallContext";
import {
  getWalletAvailabilityPayload,
  sendPaywallEvent,
} from "../../utils/paywallAnalytics";
import { getTrialOfferId } from "../../utils/paywallPricing";

import { baseInputStyle } from "../../constants/formConstants";

dayjs.extend(localeData);

const Form = ({
  country = "default",
  title = "",
  onlyApplePay = false,
  trialCost,
  paymentAmount,
  paymentCurrency = "usd",
  paymentValue,
  priceID,
  successAction,
}) => {
  const [isAllFieldsComplete, setAllFieldsComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  // card number validation
  const [isCardNumberCorrect, setCardNumberCorrect] = useState(true);
  const [isCardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardNumberErrorMessage, setCardNumberErrorMessage] = useState("");
  const [cardInputOnFocus, setCardInputOnFocus] = useState(false);
  const [isCardNumberEmpty, setCardNumberEmpty] = useState(true);

  // date validation
  const [isDateCorrect, setDateCorrect] = useState(true);
  const [isDateComplete, setDateComplete] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState("");
  const [isDateEmpty, setDateEmpty] = useState(true);
  const [dateInputOnFocus, setDateInputOnFocus] = useState(false);

  // cvv validation
  const [isCvvCorrect, setCvvCorrect] = useState(true);
  const [isCvvComplete, setCvvComplete] = useState(false);
  const [cvvErrorMessage, setCvvErrorMessage] = useState("");
  const [isCvvEmpty, setCvvEmpty] = useState(true);
  const [cvvInputOnFocus, setCvvInputOnFocus] = useState(false);

  // email validation
  const [email, setEmail] = useState("");
  const [isEmailCorrect, setEmailCorrect] = useState(true);
  const [isEmailComplete, setEmailComplete] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailInputOnFocus, setEmailInputOnFocus] = useState(false);

  const [isCvvPopupOpen, setCvvPopupOpen] = useState(false);

  // common validation error
  const [commonErrorMessage, setCommonErrorMessage] = useState("");
  // retry state (single automatic retry after failed 3DS)
  const [isRetrying, setIsRetrying] = useState(false);

  // applePay
  const walletInteractionRef = useRef(new WeakMap());
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestKey, setPaymentRequestKey] = useState(0);

  const stripe = useStripe();
  const elements = useElements();

  const dispatch = useDispatch();

  const parentBlock = useRef(null);
  const cardNumberRef = useRef(null);
  const cardFormStartedRef = useRef(false);

  const clientReferenceId = getClientReferenceId();
  const pathname = window.location?.pathname || "";
  const { paywall, paymentLabel } = getPaywallContext(pathname);
  const walletPaymentLabel = paymentLabel || DEFAULT_WALLET_PAYMENT_LABEL;
  const { acquisition_country: acquisitionCountry } = getAcquisitionCountryMetadataFromSearch(window.location.search);
  const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 46 });
  const paymentCurrencyCode = String(paymentCurrency || "usd").toLowerCase();
  const offerId = getTrialOfferId(paymentCurrencyCode);
  const metadataPriceId =
    paymentCurrencyCode === "eur" ? EU_STRIPE_PRICE_ID : STRIPE_PRICE_ID;
  const purchaseValue = Number.isFinite(Number(paymentValue))
    ? Number(paymentValue)
    : Number(trialCost);
  const computedPaymentAmount = Number.isFinite(Number(paymentAmount))
    ? Math.round(Number(paymentAmount))
    : Math.round(Number(trialCost) * 100);
  const persistTrackingContext = () => {
    try {
      if (paywall) {
        sessionStorage.setItem("lastPaywall", paywall);
      }
      if (clientReferenceId) {
        sessionStorage.setItem("clientReferenceId", clientReferenceId);
      }
    } catch (_) {}
  };

  const trackTrialSuccess = (paymentPath, status = "succeeded") => {
    sendPaywallEvent("trial_purchase_completed", {
      paymentPath,
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
        status,
      },
    });
  };

  const finishTrialPurchase = async (paymentPath, status = "succeeded") => {
    persistTrackingContext();
    trackTrialSuccess(paymentPath, status);
    await pushPurchaseEvent({
      clientReferenceId,
      paywall,
      value: purchaseValue,
      type: "trial",
    });
    await sendAmplitudePurchase({
      userId: clientReferenceId,
      revenue: purchaseValue,
      currency: paymentCurrencyCode,
      product: "Trial payment",
      paywall,
      type: "trial",
    });
  };

  const paymentIntentIdFrom = (data, fallback) =>
    String((data && (data.payment_intent_id || data.id)) || fallback || "").trim();

  const finishSuccessfulPayment = async (data, paymentPath, status = "succeeded", fallbackPaymentIntentId = "") => {
    setCommonErrorMessage("");
    setLoading(false);
    setIsRetrying(false);
    await finishTrialPurchase(paymentPath, status);
    successAction(paymentIntentIdFrom(data, fallbackPaymentIntentId));
  };

  const trackCardFormStarted = () => {
    if (onlyApplePay || cardFormStartedRef.current) return;
    cardFormStartedRef.current = true;
    sendPaywallEvent("card_form_started", {
      paymentPath: "card",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
      },
    });
  };

  const options = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        theme: "dark",
        height: "55px",
      },
    },
  };

  const onErrorTap = () => {
    setCommonErrorMessage("");
  };

  useEffect(() => {
    document.body.addEventListener("touchend", onErrorTap);
    document.body.addEventListener("click", onErrorTap);
    return () => {
      document.body.removeEventListener("touchend", onErrorTap);
      document.body.removeEventListener("click", onErrorTap);
    };
  }, []);

  useEffect(() => {
    setAllFieldsComplete(
      isDateComplete && isCvvComplete && isCardNumberComplete && isEmailComplete
    );
  }, [isDateComplete, isCvvComplete, isCardNumberComplete, isEmailComplete]);

  useEffect(() => {
    sendPaywallEvent("payment_options_seen", {
      paymentPath: onlyApplePay ? "wallet" : "card",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
      },
    });
  }, [onlyApplePay, paymentCurrencyCode, computedPaymentAmount]);

  useEffect(() => {
    if (onlyApplePay) return;
    sendPaywallEvent("card_form_opened", {
      paymentPath: "card",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
      },
    });
  }, [onlyApplePay, paymentCurrencyCode, computedPaymentAmount]);

  useEffect(() => {
    if (!stripe || !trialCost) return;

    const amount = computedPaymentAmount;
    if (!amount || amount <= 0) return;

    const walletInteraction = createWalletInteractionAnalytics({ amount, currency: paymentCurrencyCode, wallet_surface: "payment_form" });
    const pr = stripe.paymentRequest({
      country: "US",
      currency: paymentCurrencyCode,
      total: {
        label: walletPaymentLabel,
        amount,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    pr.on("cancel", () => walletInteraction.dismissed());
    walletInteractionRef.current.set(pr, walletInteraction);
    pr.canMakePayment()
      .then((res) => {
        walletInteraction.available(res);
        if (res) {
          sendPaywallEvent("wallet_available", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              ...getWalletAvailabilityPayload(res),
            },
          });
          dispatch(setApplePayAvailible(true));
          setPaymentRequest(pr); // Монтируем кнопку только если доступна
          setPaymentRequestKey((prev) => prev + 1);
        } else {
          sendPaywallEvent("wallet_unavailable", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              payment_request_supported: false,
            },
          });
          dispatch(setApplePayAvailible(false));
        }
      })
      .catch(() => {
        sendPaywallEvent("wallet_unavailable", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            reason: "can_make_payment_error",
          },
        });
        dispatch(setApplePayAvailible(false));
      });

    pr.on("paymentmethod", async (e) => {
      // A wallet handler can outlive URL navigation. Freeze this attempt's attribution now.
      const clientReferenceId = getClientReferenceId();
      const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 46 });
      walletInteraction.authorized(e?.walletName);
      setCommonErrorMessage(""); // Clear previous errors
      const now = new Date();
      now.setDate(now.getDate() + TRIAL_DAYS);
      const trialEnd = Math.floor(now.valueOf() / 1000);

      // SUBSCRIPTION CREATION DISABLED - Use payment-only endpoint
      let existingCustomerId = null;
      try {
        existingCustomerId = sessionStorage.getItem("stripeCustomerId");
      } catch {}

      const bodyObj = {
        offerId,
        amount,
        id: e.paymentMethod.id,
        email: e.payerEmail || "applegoogle@email.com",
        data: {
          currency: paymentCurrencyCode,
          client_reference_id: clientReferenceId,
          price_id: metadataPriceId,
          ...utmMetadata,
          ...(acquisitionCountry ? { acquisition_country: acquisitionCountry } : {}),
          ...(paywall ? { paywall } : {}),
        },
        ...(existingCustomerId ? { customerId: existingCustomerId } : {}),
      };

      try {
        // Use payment endpoint instead of create-payment-intent to avoid subscriptions
        const idempotencyKey = `${e.paymentMethod.id}-${amount}`;
        const response = await axios.post(`${API_URL}/payment`, bodyObj, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        const returnedCustomerId =
          response?.data?.customerId || response?.data?.customer?.id;
        if (returnedCustomerId) {
          try {
            sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
          } catch {}
        }
        
        if (response.data.success) {
          e.complete("success");
          await finishSuccessfulPayment(response.data, "wallet");
        } else if (response.data.requires_action) {
          // Close wallet sheet successfully, then run 3DS
          e.complete("success");
          sendPaywallEvent("wallet_payment_requires_action", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "confirm_card_payment",
              status: "requires_action",
            },
          });
          try {
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              response.data.client_secret
            );
            if (confirmError) {
              setCommonErrorMessage(confirmError.message);
              sendPaywallEvent("wallet_payment_failed", {
                paymentPath: "wallet",
                payload: {
                  currency: paymentCurrencyCode,
                  amount,
                  error_stage: "confirm_card_payment",
                },
              });
            } else {
              const status = paymentIntent && paymentIntent.status;
              if (
                status === "succeeded" ||
                status === "processing" ||
                status === "requires_capture"
              ) {
                await finishSuccessfulPayment(
                  response.data,
                  "wallet",
                  status,
                  paymentIntent?.id,
                );
              } else {
                setCommonErrorMessage(
                  "Payment authentication failed. Please try again."
                );
                sendPaywallEvent("wallet_payment_failed", {
                  paymentPath: "wallet",
                  payload: {
                    currency: paymentCurrencyCode,
                    amount,
                    error_stage: "confirm_card_payment",
                    status,
                  },
                });
              }
            }
          } catch (err) {
            setCommonErrorMessage("Payment authentication failed. Please try again.");
            sendPaywallEvent("wallet_payment_failed", {
              paymentPath: "wallet",
              payload: {
                currency: paymentCurrencyCode,
                amount,
                error_stage: "confirm_card_payment",
              },
            });
          }
        } else {
          e.complete("fail");
          setCommonErrorMessage(response.data.message || "Payment failed. Please try again.");
          sendPaywallEvent("wallet_payment_failed", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "payment_endpoint",
            },
          });
        }
      } catch (error) {
        console.log("Apple Pay error", error);
        e.complete("fail");
        setCommonErrorMessage("Payment failed. Please try again.");
        sendPaywallEvent("wallet_payment_failed", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            error_stage: "payment_endpoint",
          },
        });
      }
    });
  }, [stripe, acquisitionCountry, trialCost, computedPaymentAmount, paymentCurrencyCode, metadataPriceId, offerId, purchaseValue, walletPaymentLabel]);

  const onCvvClick = () => {};

  const handleSubmit = async (e, sum) => {
    setLoading(true);
    setCommonErrorMessage(""); // Clear previous errors
    setIsRetrying(false);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });
    if (!error) {
      try {
        const { id } = paymentMethod;

        const trialCostComputed = computedPaymentAmount;

        let meta = {
          currency: paymentCurrencyCode,
          client_reference_id: clientReferenceId,
          price_id: metadataPriceId,
          ...utmMetadata,
          ...(acquisitionCountry ? { acquisition_country: acquisitionCountry } : {}),
          ...(paywall ? { paywall } : {}),
        };

        let existingCustomerId = null;
        try {
          existingCustomerId = sessionStorage.getItem("stripeCustomerId");
        } catch {}

        let dataObj = {
          offerId,
          amount: trialCostComputed,
          id,
          email,
          data: meta,
          ...(existingCustomerId ? { customerId: existingCustomerId } : {}),
        };
        const idempotencyKey = `${id}-${trialCostComputed}`;
        const response = await axios.post(`${API_URL}/payment`, dataObj, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        const returnedCustomerId =
          response?.data?.customerId || response?.data?.customer?.id;
        if (returnedCustomerId) {
          try {
            sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
          } catch {}
        }
        if (response.data.success) {
          try {
            sessionStorage.setItem("stripePaymentMethodId", id);
          } catch {}
          await finishSuccessfulPayment(response.data, "card");
        } else if (response.data.requires_action) {
          sendPaywallEvent("card_payment_requires_action", {
            paymentPath: "card",
            payload: {
              currency: paymentCurrencyCode,
              amount: trialCostComputed,
              error_stage: "confirm_card_payment",
              status: "requires_action",
            },
          });
          try {
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              response.data.client_secret
            );
            if (confirmError) {
              // Auto-retry once with threeDSMode=automatic
              await handleAutomaticRetry({
                paymentMethodId: id,
                amount: trialCostComputed,
                email,
                meta,
                customerId: returnedCustomerId || existingCustomerId || null,
              });
            } else {
              const status = paymentIntent && paymentIntent.status;
              if (
                status === "succeeded" ||
                status === "processing" ||
                status === "requires_capture"
              ) {
                try {
                  sessionStorage.setItem("stripePaymentMethodId", id);
                } catch {}
                await finishSuccessfulPayment(
                  response.data,
                  "card",
                  status,
                  paymentIntent?.id,
                );
              } else {
                // Auto-retry once with threeDSMode=automatic
                await handleAutomaticRetry({
                  paymentMethodId: id,
                  amount: trialCostComputed,
                  email,
                  meta,
                  customerId: returnedCustomerId || existingCustomerId || null,
                });
              }
            }
          } catch (err) {
            // Auto-retry once with threeDSMode=automatic
            await handleAutomaticRetry({
              paymentMethodId: id,
              amount: trialCostComputed,
              email,
              meta,
              customerId: returnedCustomerId || existingCustomerId || null,
            });
          }
        } else {
          setCommonErrorMessage(response.data.message);
          setLoading(false);
          sendPaywallEvent("card_payment_failed", {
            paymentPath: "card",
            payload: {
              currency: paymentCurrencyCode,
              amount: trialCostComputed,
              error_stage: "payment_endpoint",
            },
          });
        }
      } catch (e) {
        setCommonErrorMessage(e.message);
        setLoading(false);
        sendPaywallEvent("card_payment_failed", {
          paymentPath: "card",
          payload: {
            currency: paymentCurrencyCode,
            amount: computedPaymentAmount,
            error_stage: "payment_endpoint",
          },
        });
      }
    } else {
      setCommonErrorMessage(error.message);
      setLoading(false);
      sendPaywallEvent("card_payment_failed", {
        paymentPath: "card",
        payload: {
          currency: paymentCurrencyCode,
          amount: computedPaymentAmount,
          error_stage: "create_payment_method",
        },
      });
    }
  };

  const handleAutomaticRetry = async ({
    paymentMethodId,
    amount,
    email,
    meta,
    customerId,
  }) => {
    if (!stripe) {
      setCommonErrorMessage("Payment authentication failed. Please try again.");
      setLoading(false);
      return;
    }
    // Single retry guard: if already retrying, do not start another
    if (isRetrying) {
      return;
    }
    setIsRetrying(true);
    setCommonErrorMessage("Retrying payment...");
    try {
      const retryIdempotencyKey = `retry1-${paymentMethodId}-${amount}-${Date.now()}`;
      const retryBody = {
        offerId,
        amount,
        id: paymentMethodId,
        email,
        data: meta,
        ...(customerId ? { customerId } : {}),
        threeDSMode: "automatic",
      };
      const response = await axios.post(`${API_URL}/payment`, retryBody, {
        headers: { "Idempotency-Key": retryIdempotencyKey },
      });

      const returnedCustomerId =
        response?.data?.customerId || response?.data?.customer?.id;
      if (returnedCustomerId) {
        try {
          sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
        } catch {}
      }

      if (response?.data?.success) {
        try {
          sessionStorage.setItem("stripePaymentMethodId", paymentMethodId);
        } catch {}
        await finishSuccessfulPayment(response.data, "card");
        return;
      }

      if (response?.data?.requires_action && response?.data?.client_secret) {
        sendPaywallEvent("card_payment_requires_action", {
          paymentPath: "card",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            error_stage: "confirm_card_payment_retry",
            status: "requires_action",
          },
        });
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.client_secret
        );
        if (confirmError) {
          setCommonErrorMessage("Payment authentication failed. Please try again.");
          setIsRetrying(false);
          setLoading(false);
          sendPaywallEvent("card_payment_failed", {
            paymentPath: "card",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "confirm_card_payment_retry",
            },
          });
          return;
        }
        const status = paymentIntent && paymentIntent.status;
        if (
          status === "succeeded" ||
          status === "processing" ||
          status === "requires_capture"
        ) {
          try {
            sessionStorage.setItem("stripePaymentMethodId", paymentMethodId);
          } catch {}
          await finishSuccessfulPayment(
            response.data,
            "card",
            status,
            paymentIntent?.id,
          );
          return;
        }
        setCommonErrorMessage("Payment authentication failed. Please try again.");
        setIsRetrying(false);
        setLoading(false);
        sendPaywallEvent("card_payment_failed", {
          paymentPath: "card",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            error_stage: "confirm_card_payment_retry",
            status,
          },
        });
        return;
      }

      setCommonErrorMessage(response?.data?.message || "Payment failed. Please try again.");
      setIsRetrying(false);
      setLoading(false);
      sendPaywallEvent("card_payment_failed", {
        paymentPath: "card",
        payload: {
          currency: paymentCurrencyCode,
          amount,
          error_stage: "payment_endpoint_retry",
        },
      });
    } catch (err) {
      setCommonErrorMessage("Payment failed. Please try again.");
      setIsRetrying(false);
      setLoading(false);
      sendPaywallEvent("card_payment_failed", {
        paymentPath: "card",
        payload: {
          currency: paymentCurrencyCode,
          amount,
          error_stage: "payment_endpoint_retry",
        },
      });
    }
  };

  const handleSubmitSubscription = async ({ customer }, onlySubscription) => {
    setCommonErrorMessage(""); // Clear previous errors
    setLoading(true);
    const now = new Date();
    now.setDate(now.getDate() + 7);
    let mft = Math.floor(now.valueOf() / 1000);
    const priceId = priceID;

    const subscriptionMetadata = {
      currency: "usd",
      client_reference_id: clientReferenceId,
      price_id: priceId,
      ...utmMetadata,
      ...(acquisitionCountry ? { acquisition_country: acquisitionCountry } : {}),
      ...(paywall ? { paywall } : {}),
    };
    let computedCustomer = customer;

    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });

    if (onlySubscription) {
      computedCustomer = await createLegacySubscriptionCustomer({
        email,
        paymentMethodId: result.paymentMethod.id,
        metadata: subscriptionMetadata,
      });
    }

    if (result.error) {
      setCommonErrorMessage(result.error.message);
      setLoading(false);
    } else {
      try {
        let dataObj = {
          payment_method: result.paymentMethod.id,
          email,
          product: priceId,
          metadata: subscriptionMetadata,
          customer: computedCustomer,
          billing_cycle_anchor: mft,
          trial_end: mft,
          currency: "usd",
        };

        const res = await postLegacySubscription(dataObj);
        const { client_secret, status } = res.data;

        if (status === "required_action") {
          stripe.confirmCardPayment(client_secret).then(function (result) {
            if (result.error) {
              setCommonErrorMessage(result.error.message);
              setLoading(false);
            } else {
              setCommonErrorMessage("");
              successAction();
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
          successAction();
          console.log("NEED TO SET CALLBACK SUCCESS LOGIC");
        }
      } catch (e) {
        setCommonErrorMessage("Something went wrong");
        setLoading(false);
      }
    }
  };

  const handleCustomInputChange = (
    e,
    setDataFunction,
    setCompleteFunction,
    type
  ) => {
    trackCardFormStarted();
    if (setDataFunction) {
      setDataFunction(e.target.value);
    }
    if (e.target.value.length) {
      checkValueToType(type, e.target.value, null, setCompleteFunction);
    }
    if (e.target.value.length === 0) {
      setEmailErrorMessage("");
    }
  };

  const handleInputChange = (
    e,
    setCorrectFunction,
    setCompleteFunction,
    setErrorMessageFunction,
    setEmpty
  ) => {
    trackCardFormStarted();
    setCorrectFunction(!e.error);

    if (e.error && e.error.message) {
      if (e.empty === false) {
        setEmpty(false);
      } else {
        setEmpty(true);
      }
      setErrorMessageFunction(e.error.message);
    } else {
      if (e.empty === false) {
        setEmpty(false);
      } else {
        setEmpty(true);
      }
      setErrorMessageFunction("");
    }
    setCompleteFunction(e.complete);
  };

  const handleSubmitPayAndSubscriptionClick = async () => {
    if (trialCost !== "0") {
      sendPaywallEvent("card_payment_submitted", {
        paymentPath: "card",
        payload: {
          currency: paymentCurrencyCode,
          amount: computedPaymentAmount,
        },
      });
      await handleSubmit();
    } else {
      // SUBSCRIPTION CREATION DISABLED - Uncomment line below to re-enable
      // await handleSubmitSubscription({ customer: null }, true);
      // Skip subscription creation for free trials
      setLoading(false);
      successAction();
    }
  };

  const handleInputFocus = (setFunction) => {
    trackCardFormStarted();
    setFunction(true);
  };

  const handleInputBlur = (setFunction) => {
    setFunction(false);
  };

  const handleCustomInputBlur = (
    setFocusFunction,
    length,
    e,
    setCorrectFunction,
    setCompleteFunction,
    setErrorMessageFunction,
    setDataFunction,
    type
  ) => {
    setFocusFunction(false);
    if (e.target.value.length) {
      checkValueToType(
        type,
        e.target.value,
        setCorrectFunction,
        setCompleteFunction,
        setErrorMessageFunction
      );
    }
  };

  const handleQuestionClick = () => {
    if (isCvvComplete === true) {
      return false;
    }
    const cvvPopupOpen = isCvvPopupOpen;
    setCvvPopupOpen(!cvvPopupOpen);
  };

  const setCardsIcons = () => {
    return cardsIcons;
  };

  const { innerBorderRef } = useOnOutsideClick(() => setCvvPopupOpen(false));

  const formRender = () => {
    return (
      <form id="card-form" autoComplete={"off"}>
        <CardInputWrapper
          className={"cardnumber"}
          $isValid={isCardNumberCorrect}
          $isComplete={isCardNumberComplete}
          tabIndex={0}
          ref={cardNumberRef}
        >
          <CardInputWrapper.Placeholder
            className={
              isCardNumberEmpty
                ? cardInputOnFocus
                  ? "active"
                  : null
                : "active"
            }
          >
            <Paragraph fw={500} color={"#7E969C"}>
              {texts[country]?.quiz.payment.modal.card_number_placeholder}
            </Paragraph>
          </CardInputWrapper.Placeholder>
          <CardNumberElement
            onFocus={() => handleInputFocus(setCardInputOnFocus)}
            onBlur={() => handleInputBlur(setCardInputOnFocus)}
            onChange={(e) =>
              handleInputChange(
                e,
                setCardNumberCorrect,
                setCardNumberComplete,
                setCardNumberErrorMessage,
                setCardNumberEmpty
              )
            }
            options={{
              style: {
                base: baseInputStyle,
                paddingLeft: 40,
              },
            }}
          />
          {cardNumberErrorMessage ? (
            <CardInputWrapper.Message>
              {cardNumberErrorMessage}
            </CardInputWrapper.Message>
          ) : null}
        </CardInputWrapper>
        <CardInputWrapper.Horizontal style={{ marginBottom: 16 }}>
          <div style={{ width: "49%" }}>
            <CardInputWrapper
              $isValid={isDateCorrect}
              $isComplete={isDateComplete}
            >
              <CardInputWrapper.Placeholder
                className={
                  isDateEmpty ? (dateInputOnFocus ? "active" : null) : "active"
                }
              >
                <Paragraph fw={500} color={"#7E969C"}>
                  MM/YY
                </Paragraph>
              </CardInputWrapper.Placeholder>
              <CardExpiryElement
                onFocus={() => handleInputFocus(setDateInputOnFocus)}
                onBlur={() => handleInputBlur(setDateInputOnFocus)}
                onChange={(e) =>
                  handleInputChange(
                    e,
                    setDateCorrect,
                    setDateComplete,
                    setDateErrorMessage,
                    setDateEmpty
                  )
                }
                options={{
                  style: {
                    base: baseInputStyle,
                  },
                }}
              />
            </CardInputWrapper>
            {dateErrorMessage ? (
              <CardInputWrapper.Message>
                {dateErrorMessage}
              </CardInputWrapper.Message>
            ) : null}
          </div>
          <div style={{ width: "49%" }}>
            <CardInputWrapper $isValid={isCvvCorrect} $isComplete={isCvvComplete}>
              <CardInputWrapper.Placeholder
                className={
                  isCvvEmpty ? (cvvInputOnFocus ? "active" : null) : "active"
                }
              >
                <Paragraph fw={500} color={"#7E969C"}>
                  CVC
                </Paragraph>
              </CardInputWrapper.Placeholder>
              <CardCvcElement
                onFocus={() => handleInputFocus(setCvvInputOnFocus)}
                onBlur={() => handleInputBlur(setCvvInputOnFocus)}
                onChange={(e) =>
                  handleInputChange(
                    e,
                    setCvvCorrect,
                    setCvvComplete,
                    setCvvErrorMessage,
                    setCvvEmpty
                  )
                }
                options={{
                  style: {
                    base: baseInputStyle,
                  },
                }}
              />
              <CardInputWrapper.Icon
                className={"cvv"}
                onClick={handleQuestionClick}
              >
                <img src={questionIcon} alt="" />
              </CardInputWrapper.Icon>
              {isCvvPopupOpen ? (
                <CardInputWrapper.Tooltip
                  ref={innerBorderRef}
                  onClick={onCvvClick}
                >
                  <img src={cvv} alt="" style={{ marginBottom: 9 }} />
                  <CardInputWrapper.Tooltip.Arrow />
                </CardInputWrapper.Tooltip>
              ) : null}
            </CardInputWrapper>
            {cvvErrorMessage ? (
              <CardInputWrapper.Message>
                {cvvErrorMessage}
              </CardInputWrapper.Message>
            ) : null}
          </div>
        </CardInputWrapper.Horizontal>
        <CardInputWrapper
          $isValid={isEmailCorrect}
          $isComplete={isEmailComplete}
          style={{ marginBottom: 16 }}
        >
          <CardInputWrapper.Placeholder
            className={
              !email.length ? (emailInputOnFocus ? "active" : null) : "active"
            }
          >
            <Paragraph fw={500} color={"#7E969C"}>
              Email
            </Paragraph>
          </CardInputWrapper.Placeholder>
          <input
            type="text"
            value={email}
            onFocus={() => handleInputFocus(setEmailInputOnFocus)}
            onBlur={(e) =>
              handleCustomInputBlur(
                setEmailInputOnFocus,
                4,
                e,
                setEmailCorrect,
                setEmailComplete,
                setEmailErrorMessage,
                setEmail,
                "email"
              )
            }
            onChange={(e) =>
              handleCustomInputChange(
                e,
                setEmail,
                setEmailComplete,
                "email-change"
              )
            }
          />
          {emailErrorMessage ? (
            <CardInputWrapper.Message>
              {emailErrorMessage}
            </CardInputWrapper.Message>
          ) : null}
        </CardInputWrapper>
      </form>
    );
  };

  return (
    <div className={``}>
      <FormBlock ref={parentBlock}>
        {title && (
          <Paragraph
            fs={24}
            lh={31}
            fw={600}
            style={{ marginBottom: 10 }}
            className="mt-2"
          >
            {title}
          </Paragraph>
        )}

        <FormBlock.Apple>
          {paymentRequest && (
            <div
              key={paymentRequestKey}
              style={{
                marginBottom: 8,
                overflow: "hidden",
                borderRadius: 8,
              }}
              className={`!relative ${loading ? "!pointer-events-none opacity-50" : ""}`}
            >
              <PaymentRequestButtonElement
                onClick={() => walletInteractionRef.current.get(paymentRequest)?.clicked()}
                options={options}
                className="apple-google-pay"
              />
            </div>
          )}
        </FormBlock.Apple>
        {!onlyApplePay && (
          <div>
            <img src={setCardsIcons()} alt="cards" className="mb-4 w-full" />
          </div>
        )}

        <FormBlock.Form>
          {!onlyApplePay && formRender()}
        </FormBlock.Form>

        {!onlyApplePay && (
          <div className="mb-4">
            <FormBlock.Button>
              <Button
                buttonStyles={
                  "bg-[#a48256] !font-[800] !text-[18px] text-[#ffffff] mb-4"
                }
                action={(e) => handleSubmitPayAndSubscriptionClick(e)}
                text={texts[country]?.quiz.payment.modal.button_text}
                icon={buttonIconDisabled}
                disabledIcon={buttonIconDisabled}
                isButtonDisabled={!isAllFieldsComplete || loading || isRetrying}
                isLoading={loading || isRetrying}
              />
            </FormBlock.Button>
          </div>
        )}

        {commonErrorMessage ? (
          <CardInputWrapper.Message.Common className={"mb-4"}>
            {commonErrorMessage}
          </CardInputWrapper.Message.Common>
        ) : null}
      </FormBlock>
    </div>
  );
};

export default Form;
